import { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import { Loader2, ArrowLeft, Network, Search, X, Compass, ChevronRight, ChevronLeft } from "lucide-react";
import ConceptDetailPanel from "../components/ConceptDetailPanel";

/**
 * KnowledgeGraph — a hand-drawn constellation of the twelve worlds.
 *
 * Data comes from GET /api/curriculum/graph.  We run a lightweight force
 * simulation in pure JavaScript (no d3 dependency) so the SVG feels warm
 * and controllable rather than a canned d3 look:
 *   - repulsion between all nodes
 *   - spring toward each edge target
 *   - weak centring gravity
 *   - fixed a few dozen ticks so layout is stable across renders
 */
export default function KnowledgeGraph() {
  const [mode, setMode] = useState("worlds"); // "worlds" | "concepts"
  const [data, setData] = useState(null);
  const [positioned, setPositioned] = useState(null);
  const [hover, setHover] = useState(null);
  const [exploredSet, setExploredSet] = useState(() => new Set());
  const [indiaSet, setIndiaSet] = useState(() => new Set());
  const [query, setQuery] = useState("");
  const [focusedId, setFocusedId] = useState(null);
  const [conceptOpen, setConceptOpen] = useState(null);   // term string or null
  const [tour, setTour] = useState(null);   // {title, intro, stops:[{n,term,caption}]}
  const [tourStop, setTourStop] = useState(0); // -1 = closed, 0..N-1 stops
  const [tourStarted, setTourStarted] = useState(false);
  const [tourHistory, setTourHistory] = useState([]);
  const [historyStop, setHistoryStop] = useState(0);  // for the mini-carousel
  const [ribbon, setRibbon] = useState(null);
  const [ribbonHistory, setRibbonHistory] = useState([]);
  const svgRef = useRef(null);

  useEffect(() => {
    setData(null);
    setPositioned(null);
    setFocusedId(null);
    setTourStop(-1);
    setTourStarted(false);
    const url = mode === "concepts"
      ? "/curriculum/concept-graph?top=36"
      : "/curriculum/graph?anchors_per_world=5";
    api.get(url)
      .then(({ data }) => setData(data))
      .catch(() => setData({ nodes: [], edges: [] }));
    // Fetch explored slugs — silent-fail for anons
    api.get("/progress/explored-slugs/me")
      .then(({ data }) => setExploredSet(new Set(data.slugs || [])))
      .catch(() => {});
    // Fetch slugs with cached India depth — public
    api.get("/kb/chapters/depth/cached-slugs?level=india")
      .then(({ data }) => setIndiaSet(new Set(data.slugs || [])))
      .catch(() => {});
    // Fetch season ribbon (independent of mode)
    api.get("/curriculum/season-ribbon")
      .then(({ data }) => setRibbon(data && data.active ? data : null))
      .catch(() => setRibbon(null));
    // Fetch ribbon history for the small marquee below the active ribbon.
    // Pull one extra so the marquee can always render up-to-3 *inactive*
    // pills after filtering the active one out.
    api.get("/curriculum/season-ribbon/history?limit=4")
      .then(({ data }) => setRibbonHistory(data.ribbons || []))
      .catch(() => setRibbonHistory([]));
    // Fetch Ms. Pradhan's concept tour — only used in Concepts mode
    if (mode === "concepts") {
      api.get("/curriculum/concept-tour")
        .then(({ data }) => setTour(data))
        .catch(() => setTour(null));
      // Load history for the small carousel of past openings
      api.get("/curriculum/concept-tour/history?limit=3")
        .then(({ data }) => setTourHistory(data.tours || []))
        .catch(() => setTourHistory([]));
    }
  }, [mode]);

  // Auto-open the tour the first time a learner enters Concepts mode.
  useEffect(() => {
    if (mode !== "concepts" || !tour || !tour.stops?.length || tourStarted) return;
    try {
      const seen = localStorage.getItem("dpa.graph.concept.tour.seen");
      if (!seen) {
        setTourStop(0);
        setTourStarted(true);
      }
    } catch {
      // localStorage unavailable — just skip auto-open
    }
  }, [mode, tour, tourStarted]);

  const tourActive = mode === "concepts" && tour && tourStop >= 0 && tour.stops?.[tourStop];
  const tourTerm = tourActive ? tour.stops[tourStop].term : null;
  const closeTour = () => {
    setTourStop(-1);
    try { localStorage.setItem("dpa.graph.concept.tour.seen", "1"); } catch { /* ignore */ }
  };

  // Run force simulation once we have data.
  useEffect(() => {
    if (!data || !data.nodes.length) return;
    const W = 1400, H = 900, cx = W / 2, cy = H / 2;

    let nodes;
    if (mode === "concepts") {
      // Concept mode: nodes on a jittered circle by frequency rank
      nodes = data.nodes.map((n, i) => {
        const angle = (i / data.nodes.length) * Math.PI * 2;
        const r = 260 + (Math.random() - 0.5) * 80;
        return { ...n, x: cx + Math.cos(angle) * r, y: cy + Math.sin(angle) * r, vx: 0, vy: 0 };
      });
    } else {
      // Worlds mode: worlds on inner circle, chapters near their world
      const worlds = data.nodes.filter((n) => n.kind === "world");
      nodes = data.nodes.map((n) => {
        if (n.kind === "world") {
          const angle = (n.order / worlds.length) * Math.PI * 2;
          const r = 260;
          return { ...n, x: cx + Math.cos(angle) * r, y: cy + Math.sin(angle) * r, vx: 0, vy: 0, fixed: false };
        }
        const w = worlds.find((w) => w.slug === n.world_slug);
        const wa = w ? (w.order / worlds.length) * Math.PI * 2 : Math.random() * Math.PI * 2;
        const jitter = (Math.random() - 0.5) * 60;
        return { ...n, x: cx + Math.cos(wa) * (420 + jitter), y: cy + Math.sin(wa) * (420 + jitter), vx: 0, vy: 0, fixed: false };
      });
    }
    const byId = new Map(nodes.map((n) => [n.id, n]));

    // Force simulation — tuned per mode
    const TICKS = 220;
    const REPULSION = mode === "concepts" ? 4200 : 3600;
    const SPRING_K = mode === "concepts" ? 0.016 : 0.012;
    const SPRING_LEN_W = 220;
    const SPRING_LEN_C = 90;
    const SPRING_LEN_CONCEPT = 160;
    const GRAVITY = 0.0016;
    const DAMPING = 0.72;
    for (let t = 0; t < TICKS; t++) {
      for (const n of nodes) { n.fx = 0; n.fy = 0; }
      for (let i = 0; i < nodes.length; i++) {
        for (let j = i + 1; j < nodes.length; j++) {
          const a = nodes[i], b = nodes[j];
          let dx = a.x - b.x, dy = a.y - b.y;
          let d2 = dx * dx + dy * dy;
          if (d2 < 1) { d2 = 1; dx = Math.random(); dy = Math.random(); }
          const f = REPULSION / d2;
          const d = Math.sqrt(d2);
          const fx = (dx / d) * f, fy = (dy / d) * f;
          a.fx += fx; a.fy += fy;
          b.fx -= fx; b.fy -= fy;
        }
      }
      for (const e of data.edges) {
        const a = byId.get(e.source), b = byId.get(e.target);
        if (!a || !b) continue;
        const dx = b.x - a.x, dy = b.y - a.y;
        const d = Math.sqrt(dx * dx + dy * dy) || 0.01;
        let targetLen, strength;
        if (mode === "concepts") {
          targetLen = SPRING_LEN_CONCEPT;
          // Heavier co-occurrence = shorter spring
          strength = SPRING_K * Math.min(2.5, (e.weight || 1) / 5 + 0.6);
        } else {
          targetLen = (a.kind === "world" && b.kind === "world") ? SPRING_LEN_W : SPRING_LEN_C;
          strength = e.kind === "prereq" ? SPRING_K * 1.4 : e.kind === "related" ? SPRING_K * 0.8 : SPRING_K;
        }
        const f = (d - targetLen) * strength;
        const fx = (dx / d) * f, fy = (dy / d) * f;
        a.fx += fx; a.fy += fy;
        b.fx -= fx; b.fy -= fy;
      }
      for (const n of nodes) {
        n.fx += (cx - n.x) * GRAVITY;
        n.fy += (cy - n.y) * GRAVITY;
      }
      // Integrate
      for (const n of nodes) {
        n.vx = (n.vx + n.fx) * DAMPING;
        n.vy = (n.vy + n.fy) * DAMPING;
        n.x += n.vx;
        n.y += n.vy;
      }
    }

    setPositioned({ nodes, edges: data.edges, byId });
  }, [data]);

  // Accent colours per world
  const worldColour = useMemo(() => ({
    "foundations": "#F4A261",
    "programming": "#E76F51",
    "data-structures-algorithms": "#2A9D8F",
    "architecture": "#1F1A17",
    "software-engineering": "#F4A261",
    "internet-web": "#2A9D8F",
    "mathematics": "#E76F51",
    "ai": "#2A9D8F",
    "security": "#1F1A17",
    "hci": "#F4A261",
    "computational-science": "#E76F51",
    "biology": "#2A9D8F",
  }), []);

  if (!data || !positioned) {
    return (
      <div className="container-narrow py-24 text-center text-ink2" data-testid="graph-loading">
        <Loader2 className="w-6 h-6 mx-auto animate-spin" />
        <div className="mt-3 text-sm">Laying out the twelve worlds…</div>
      </div>
    );
  }

  const { nodes, edges, byId } = positioned;

  // Search matches — case-insensitive substring on labels
  const q = query.trim().toLowerCase();
  const matches = q
    ? nodes.filter((n) => (n.label || "").toLowerCase().includes(q)).slice(0, 8)
    : [];
  const focused = focusedId ? byId.get(focusedId) : null;

  // When the tour is active, pin focus onto the current stop's concept node
  // so the SVG zooms to it and the pulsing halo highlights it.  Silent-noop
  // if the term isn't in the current node set.
  const tourNode = tourTerm ? byId.get(`concept:${tourTerm}`) : null;
  const activeFocus = focused || tourNode;

  // ViewBox: default is 0 0 1400 900.  When focused, zoom to 600×400 around the node.
  const viewBox = activeFocus
    ? `${Math.max(0, Math.min(800, activeFocus.x - 300))} ${Math.max(0, Math.min(500, activeFocus.y - 200))} 600 400`
    : "0 0 1400 900";

  return (
    <div className="container-narrow py-10 md:py-14" data-testid="knowledge-graph-page">
      <Link to="/learn" className="inline-flex items-center gap-1.5 text-sm text-ink2 hover:text-ink mb-6">
        <ArrowLeft className="w-4 h-4" /> The twelve worlds
      </Link>

      {ribbon && ribbon.text && (
        <div
          className="mb-6 rounded-full border-2 border-ink bg-marigold text-cream px-5 py-2.5 shadow-[4px_4px_0_0_#1F1A17] inline-flex items-center gap-3 max-w-full"
          data-testid="graph-season-ribbon"
        >
          <span className="text-[10px] uppercase tracking-[0.32em] font-bold opacity-90 flex-shrink-0">This season</span>
          <span className="font-heading text-sm md:text-base leading-tight" data-testid="graph-season-ribbon-text">
            {ribbon.text}
          </span>
          {ribbon.curator && (
            <span className="ml-auto text-[9px] font-mono uppercase tracking-widest opacity-80 hidden md:inline flex-shrink-0" data-testid="graph-season-ribbon-curator">
              · dressed by {ribbon.curator}
            </span>
          )}
        </div>
      )}
      {(() => {
        const past = ribbonHistory.filter((r) => !r.active).slice(0, 3);
        if (past.length === 0) return null;
        return (
          <div
            className="mb-6 flex items-center gap-2 flex-wrap text-[10px] font-mono uppercase tracking-widest text-ink2/70"
            data-testid="graph-season-ribbon-history"
          >
            <span className="text-marigold font-bold">Previously:</span>
            {past.map((r, i) => (
              <span
                key={r.id || i}
                className="inline-flex items-center gap-1.5 rounded-full border border-ink/25 bg-cream/60 px-2.5 py-1"
                data-testid={`graph-season-ribbon-history-${i}`}
              >
                <span className="text-ink font-heading normal-case tracking-normal text-[11px] max-w-[240px] truncate">{r.text}</span>
                <span className="text-ink2/60">· {r.curator}</span>
              </span>
            ))}
          </div>
        );
      })()}

      <div className="mb-6 flex items-start gap-4 flex-wrap">
        <div className="w-12 h-12 rounded-2xl bg-marigold/25 text-marigold flex items-center justify-center flex-shrink-0">
          <Network className="w-6 h-6" />
        </div>
        <div className="flex-1 min-w-[260px]">
          <div className="text-[11px] uppercase tracking-[0.28em] font-bold text-marigold mb-1">The Constellation</div>
          <h1 className="font-heading text-4xl md:text-5xl font-semibold text-ink leading-[1.05] mb-2">
            {mode === "concepts" ? <>Ideas that appear <span className="italic">everywhere</span>.</> : <>Recursion touches trees. Trees touch compilers.</>}
          </h1>
          <p className="text-ink2 max-w-2xl">
            {mode === "concepts"
              ? "A concept-only view — nodes are ideas, edges are the chapters that hold two ideas together."
              : "A hand-drawn map of how the twelve worlds connect. Anchor chapters hang off each world like leaves."}
          </p>
        </div>
        {/* Mode toggle */}
        <div className="inline-flex items-center gap-0 rounded-full border-2 border-ink bg-cream p-1 shadow-[4px_4px_0_0_#1F1A17]" data-testid="graph-mode-toggle">
          <button
            onClick={() => setMode("worlds")}
            className={`text-[10px] uppercase tracking-[0.22em] font-bold px-3 py-1.5 rounded-full transition ${mode === "worlds" ? "bg-ink text-cream" : "text-ink2 hover:text-ink"}`}
            data-testid="graph-mode-worlds"
          >
            Worlds
          </button>
          <button
            onClick={() => setMode("concepts")}
            className={`text-[10px] uppercase tracking-[0.22em] font-bold px-3 py-1.5 rounded-full transition ${mode === "concepts" ? "bg-ink text-cream" : "text-ink2 hover:text-ink"}`}
            data-testid="graph-mode-concepts"
          >
            Concepts
          </button>
        </div>
        {mode === "concepts" && tour && tour.stops?.length > 0 && !tourActive && (
          <button
            onClick={() => setTourStop(0)}
            className="btn-outline-ink text-xs inline-flex items-center gap-1.5"
            data-testid="graph-tour-start"
          >
            <Compass className="w-3.5 h-3.5" /> Take Ms. Pradhan's tour
          </button>
        )}
      </div>

      <div className="rounded-3xl border-2 border-ink bg-cream shadow-[8px_8px_0_0_#1F1A17] overflow-hidden relative">
        {/* Search overlay */}
        <div className="absolute top-3 left-3 right-3 md:right-auto md:w-[400px] z-10" data-testid="graph-search">
          <div className="relative rounded-2xl border-2 border-ink bg-cream shadow-[4px_4px_0_0_#1F1A17]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-marigold" />
            <input
              type="text"
              value={query}
              onChange={(e) => { setQuery(e.target.value); setFocusedId(null); }}
              placeholder="Find a world or chapter…"
              className="w-full py-2 pl-9 pr-9 text-sm bg-transparent focus:outline-none font-heading"
              data-testid="graph-search-input"
            />
            {query && (
              <button
                onClick={() => { setQuery(""); setFocusedId(null); }}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-ink2 hover:text-ink"
                data-testid="graph-search-clear"
              >
                <X className="w-4 h-4" />
              </button>
            )}
          </div>
          {q && matches.length > 0 && !focused && (
            <ul className="mt-1 rounded-2xl border-2 border-ink bg-cream shadow-[4px_4px_0_0_#1F1A17] overflow-hidden max-h-72 overflow-y-auto" data-testid="graph-search-results">
              {matches.map((n) => (
                <li key={n.id}>
                  <button
                    onClick={() => setFocusedId(n.id)}
                    className="w-full text-left px-3 py-2 flex items-center gap-2 hover:bg-marigold/10 border-b border-ink/10 last:border-b-0"
                    data-testid={`graph-search-match-${n.slug}`}
                  >
                    <span className={`text-[9px] font-mono font-bold uppercase tracking-widest ${n.kind === "world" ? "text-marigold" : "text-ink2/60"}`}>
                      {n.kind === "world" ? "world" : n.code || "chapter"}
                    </span>
                    <span className="text-sm text-ink truncate">{n.label}</span>
                  </button>
                </li>
              ))}
            </ul>
          )}
          {q && matches.length === 0 && (
            <div className="mt-1 rounded-2xl border-2 border-ink bg-cream px-3 py-2 text-xs text-ink2 italic" data-testid="graph-search-empty">
              Nothing matched — try a different word.
            </div>
          )}
          {focused && (
            <div className="mt-1 rounded-2xl border-2 border-ink bg-marigold/[0.08] shadow-[4px_4px_0_0_#1F1A17] p-3" data-testid="graph-search-focused">
              <div className="text-[10px] uppercase tracking-widest font-bold text-marigold mb-1">Focused on</div>
              <div className="font-heading text-base font-semibold text-ink leading-tight mb-2">{focused.label}</div>
              <div className="flex items-center gap-2">
                <Link
                  to={focused.kind === "world" ? `/learn/graph/${focused.slug}` : `/library/${focused.slug}`}
                  className="btn-primary text-xs inline-flex items-center gap-1.5"
                >
                  Open →
                </Link>
                <button onClick={() => setFocusedId(null)} className="text-xs text-ink2 hover:text-ink underline">
                  Zoom out
                </button>
              </div>
            </div>
          )}
        </div>

        <svg
          ref={svgRef}
          viewBox={viewBox}
          className="w-full h-[65vh] min-h-[540px] bg-cream transition-[viewBox] duration-500"
          data-testid="knowledge-graph-svg"
          role="img"
          aria-label="Constellation of the twelve worlds"
        >
          {/* Edges */}
          <g>
            {edges.map((e, i) => {
              const a = byId.get(e.source), b = byId.get(e.target);
              if (!a || !b) return null;
              // Concept-mode: single teal stroke weighted by co-occurrence count
              if (e.kind === "co") {
                const w = Math.max(0.6, Math.min(2.5, Math.log2((e.weight || 1) + 1)));
                return (
                  <line
                    key={`co-${e.source}-${e.target}-${i}`}
                    x1={a.x} y1={a.y} x2={b.x} y2={b.y}
                    stroke="#2A9D8F" strokeWidth={w} opacity={0.35}
                  />
                );
              }
              const stroke = e.kind === "prereq" ? "#E76F51" : e.kind === "related" ? "#2A9D8F" : "#1F1A17";
              const opacity = e.kind === "contains" ? 0.18 : e.kind === "prereq" ? 0.55 : 0.38;
              const strokeDasharray = e.kind === "related" ? "5 5" : e.kind === "contains" ? "2 4" : undefined;
              return (
                <line
                  key={`${e.kind}-${e.source}-${e.target}-${i}`}
                  x1={a.x} y1={a.y} x2={b.x} y2={b.y}
                  stroke={stroke}
                  strokeWidth={e.kind === "prereq" ? 2 : 1.2}
                  strokeDasharray={strokeDasharray}
                  opacity={opacity}
                />
              );
            })}
          </g>

          {/* Nodes */}
          <g>
            {nodes.map((n) => {
              const isConcept = n.kind === "concept";
              const isWorld = n.kind === "world";
              const isExplored = !isWorld && !isConcept && exploredSet.has(n.slug);
              const hasIndia = !isWorld && !isConcept && indiaSet.has(n.slug);
              const worldExplored = isWorld && nodes.some((c) => c.kind === "chapter" && c.world_slug === n.slug && exploredSet.has(c.slug));
              const conceptR = isConcept ? Math.min(28, Math.max(10, n.size || 12)) : 0;
              const fill = isWorld
                ? (worldColour[n.slug] || "#1F1A17")
                : isConcept
                  ? "#F4A261"
                  : (isExplored ? "#F4A261" : "#FDF6E7");
              const stroke = "#1F1A17";
              const r = isWorld ? 28 : isConcept ? conceptR : (isExplored ? 9 : 6);
              const href = isWorld ? `/learn/graph/${n.slug}` : isConcept ? null : `/library/${n.slug}`;
              const onClick = isConcept ? (e) => { e.preventDefault(); setConceptOpen(n.slug); } : undefined;
              return (
                <g
                  key={n.id}
                  transform={`translate(${n.x} ${n.y})`}
                  onMouseEnter={() => setHover(n)}
                  onMouseLeave={() => setHover((h) => (h && h.id === n.id ? null : h))}
                  style={{ cursor: "pointer" }}
                >
                  <a href={href || "#"} onClick={onClick} data-testid={`graph-node-${n.slug}`}>
                    {(focusedId === n.id || (tourNode && tourNode.id === n.id)) && (
                      <>
                        <circle r={r + 26} fill="none" stroke="#E76F51" strokeWidth={2.5} opacity={0.85}>
                          <animate attributeName="r" from={r + 12} to={r + 32} dur="1.4s" repeatCount="indefinite" />
                          <animate attributeName="opacity" from="0.9" to="0.1" dur="1.4s" repeatCount="indefinite" />
                        </circle>
                        <circle r={r + 16} fill="#F4A261" opacity={0.35} data-testid={`graph-focused-ring-${n.slug}`} />
                      </>
                    )}
                    {(isExplored || worldExplored) && (
                      <circle r={r + 10} fill="#F4A261" opacity={0.28} data-testid={`graph-explored-halo-${n.slug}`} />
                    )}
                    <circle r={r + 4} fill="#1F1A17" opacity={isWorld ? 0.15 : isConcept ? 0.12 : 0.08} />
                    <circle
                      r={r}
                      fill={fill}
                      stroke={stroke}
                      strokeWidth={isWorld ? 2.5 : isConcept ? 2 : 1.5}
                    />
                    {hasIndia && (
                      <g transform={`translate(${r + 2} ${-r - 2})`} data-testid={`graph-india-marker-${n.slug}`} style={{ pointerEvents: "none" }}>
                        {/* Ashoka Chakra saffron/white/green dot — three horizontal bands */}
                        <circle r={5} fill="#FDF6E7" stroke="#1F1A17" strokeWidth={1} />
                        <path d={`M -4 -1.6 A 5 5 0 0 1 4 -1.6 Z`} fill="#FF9933" />
                        <path d={`M -4 1.6 A 5 5 0 0 0 4 1.6 Z`} fill="#138808" />
                        <circle r={0.9} fill="#1F1A17" />
                      </g>
                    )}
                    {isConcept && (
                      <text
                        y={r + 18}
                        textAnchor="middle"
                        style={{
                          fontFamily: "Spectral, serif",
                          fontWeight: 600,
                          fontSize: 14,
                          fill: "#1F1A17",
                          pointerEvents: "none",
                        }}
                      >
                        {n.label}
                      </text>
                    )}
                    {isWorld && (
                      <text
                        y={r + 22}
                        textAnchor="middle"
                        style={{
                          fontFamily: "Spectral, serif",
                          fontWeight: 600,
                          fontSize: 15,
                          fill: "#1F1A17",
                          pointerEvents: "none",
                        }}
                      >
                        {n.label}
                      </text>
                    )}
                    {isWorld && (
                      <text
                        y={-r - 10}
                        textAnchor="middle"
                        style={{
                          fontFamily: "ui-monospace, monospace",
                          fontSize: 9,
                          letterSpacing: 2,
                          fill: "#E76F51",
                          textTransform: "uppercase",
                          pointerEvents: "none",
                        }}
                      >
                        {n.kicker}
                      </text>
                    )}
                  </a>
                </g>
              );
            })}
          </g>

          {/* Hover tooltip */}
          {hover && hover.kind === "chapter" && (
            <g transform={`translate(${Math.min(hover.x + 12, 1200)} ${Math.max(hover.y - 34, 20)})`}>
              <rect x={0} y={0} rx={8} ry={8} width={Math.min(260, hover.label.length * 7 + 20)} height={26} fill="#1F1A17" />
              <text x={10} y={18} style={{ fill: "#FDF6E7", fontSize: 12, fontFamily: "Outfit, sans-serif" }}>
                {hover.label.length > 34 ? hover.label.slice(0, 32) + "…" : hover.label}
              </text>
            </g>
          )}
        </svg>

        {/* Concept tour history — small carousel of past admin walks */}
        {mode === "concepts" && tourHistory.length > 0 && !tourActive && (
          <div
            className="absolute left-3 right-3 md:left-auto md:right-3 top-3 md:w-[340px] z-20 rounded-2xl border-2 border-ink bg-cream shadow-[4px_4px_0_0_#1F1A17] p-3"
            data-testid="graph-tour-history"
          >
            <div className="flex items-center gap-2 mb-1.5">
              <div className="text-[9px] uppercase tracking-[0.28em] font-bold text-marigold inline-flex items-center gap-1">
                <Compass className="w-3 h-3" /> Past walks
              </div>
              <div className="ml-auto text-[10px] font-mono text-ink2/60">
                {historyStop + 1} / {tourHistory.length}
              </div>
            </div>
            <div className="text-[10px] font-mono uppercase tracking-widest text-ink2 mb-0.5" data-testid={`graph-tour-history-curator-${historyStop}`}>
              curated by {tourHistory[historyStop]?.curator || "admin"}
            </div>
            <div className="font-heading text-sm font-semibold text-ink leading-snug mb-1.5 line-clamp-2" data-testid={`graph-tour-history-title-${historyStop}`}>
              {tourHistory[historyStop]?.title || "(untitled)"}
            </div>
            <div className="flex items-center gap-1 mb-2 flex-wrap">
              {(tourHistory[historyStop]?.stops || []).slice(0, 5).map((s, i) => (
                <span key={i} className="text-[9px] font-mono uppercase tracking-widest px-1.5 py-0.5 rounded-full border border-ink/25 text-ink2">
                  {s.term}
                </span>
              ))}
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setHistoryStop((s) => (s - 1 + tourHistory.length) % tourHistory.length)}
                disabled={tourHistory.length <= 1}
                className="text-ink2 hover:text-marigold disabled:opacity-30"
                data-testid="graph-tour-history-prev"
                aria-label="Previous walk"
              >
                <ChevronLeft className="w-4 h-4" />
              </button>
              <button
                onClick={() => {
                  const h = tourHistory[historyStop];
                  setTour({
                    title: h.title || "A past walk",
                    intro: h.intro || "One of the previous weekly openings.",
                    stops: (h.stops || []).map((s, i) => ({ n: i + 1, term: s.term, caption: s.caption })),
                    stop_count: (h.stops || []).length,
                    curated_by: h.curator || "admin",
                  });
                  setTourStop(0);
                }}
                className="btn-primary text-[10px] uppercase tracking-widest inline-flex items-center gap-1 flex-1 justify-center"
                data-testid={`graph-tour-history-replay-${historyStop}`}
              >
                <Compass className="w-3 h-3" /> Replay this walk
              </button>
              <button
                onClick={() => setHistoryStop((s) => (s + 1) % tourHistory.length)}
                disabled={tourHistory.length <= 1}
                className="text-ink2 hover:text-marigold disabled:opacity-30"
                data-testid="graph-tour-history-next"
                aria-label="Next walk"
              >
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        )}


        {/* Concept tour overlay */}
        {tourActive && (
          <div
            className="absolute left-3 right-3 md:left-auto md:right-3 bottom-20 md:w-[380px] z-20 rounded-2xl border-2 border-ink bg-cream shadow-[6px_6px_0_0_#1F1A17] p-4"
            data-testid="graph-tour-panel"
          >
            <div className="flex items-center gap-2 mb-2">
              <div className="text-[9px] uppercase tracking-[0.28em] font-bold text-marigold inline-flex items-center gap-1">
                <Compass className="w-3 h-3" /> Ms. Pradhan's tour
              </div>
              <div className="ml-auto text-[10px] font-mono text-ink2/60">
                Stop {tourStop + 1} of {tour.stops.length}
              </div>
              <button
                onClick={closeTour}
                className="text-ink2 hover:text-ink"
                data-testid="graph-tour-close"
                aria-label="Close tour"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
            <div className="font-heading text-xl font-semibold text-ink capitalize mb-1" data-testid="graph-tour-term">
              {tour.stops[tourStop].term}
            </div>
            <p className="text-sm text-ink2 leading-snug mb-3" data-testid="graph-tour-caption">
              {tour.stops[tourStop].caption}
            </p>
            <div className="flex items-center gap-2 flex-wrap">
              <button
                onClick={() => setTourStop((s) => Math.max(0, s - 1))}
                disabled={tourStop === 0}
                className="btn-outline-ink text-xs inline-flex items-center gap-1 disabled:opacity-40"
                data-testid="graph-tour-prev"
              >
                <ChevronLeft className="w-3.5 h-3.5" /> Back
              </button>
              {tourStop < tour.stops.length - 1 ? (
                <button
                  onClick={() => setTourStop((s) => s + 1)}
                  className="btn-primary text-xs inline-flex items-center gap-1"
                  data-testid="graph-tour-next"
                >
                  Next <ChevronRight className="w-3.5 h-3.5" />
                </button>
              ) : (
                <button
                  onClick={closeTour}
                  className="btn-primary text-xs inline-flex items-center gap-1"
                  data-testid="graph-tour-finish"
                >
                  Finish
                </button>
              )}
              <button
                onClick={() => { setConceptOpen(tour.stops[tourStop].term); }}
                className="text-xs text-ink2 hover:text-marigold underline ml-auto"
                data-testid="graph-tour-open-concept"
              >
                Open this concept →
              </button>
            </div>
          </div>
        )}

        {/* Legend */}
        <div className="border-t-2 border-ink px-6 py-4 flex flex-wrap items-center gap-5 text-xs text-ink2 bg-cream">
          {mode === "concepts" ? (
            <LegendSwatch color="#2A9D8F" label="Concept co-occurs in a chapter" solid />
          ) : (
            <>
              <LegendSwatch color="#E76F51" label="Prerequisite" solid />
              <LegendSwatch color="#2A9D8F" label="Related" dashed />
              <LegendSwatch color="#1F1A17" label="World contains chapter" tinyDashed />
            </>
          )}
          <div className="ml-auto font-mono text-[10px] uppercase tracking-[0.22em]">
            {exploredSet.size > 0 && <span className="text-marigold mr-3">◉ {exploredSet.size} chapters glowing = your reads</span>}
            {mode !== "concepts" && indiaSet.size > 0 && (
              <span className="text-ink2 mr-3" data-testid="graph-india-count">
                <span style={{ display: "inline-block", width: 8, height: 8, background: "linear-gradient(to bottom, #FF9933 33%, #FDF6E7 33% 66%, #138808 66%)", borderRadius: "50%", verticalAlign: "middle", marginRight: 4, border: "1px solid #1F1A17" }} />
                {indiaSet.size} with an Indian twin
              </span>
            )}
            {mode === "concepts"
              ? <>{nodes.length} concepts · {edges.length} co-occurrences</>
              : <>{nodes.filter(n => n.kind === "world").length} worlds · {nodes.filter(n => n.kind === "chapter").length} anchor chapters</>}
          </div>
        </div>
      </div>
      {conceptOpen && (
        <ConceptDetailPanel term={conceptOpen} onClose={() => setConceptOpen(null)} />
      )}
    </div>
  );
}

function LegendSwatch({ color, label, solid, dashed, tinyDashed }) {
  return (
    <div className="inline-flex items-center gap-2">
      <svg width={34} height={10}>
        <line
          x1={0} y1={5} x2={34} y2={5}
          stroke={color}
          strokeWidth={solid ? 2 : 1.2}
          strokeDasharray={dashed ? "5 5" : tinyDashed ? "2 4" : undefined}
        />
      </svg>
      <span>{label}</span>
    </div>
  );
}
